import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTokenForRequest } from '../../utils/apiAuth';
import './StudentVideoStats.css';

// Global axios interceptor for 401 Unauthorized
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      window.location.href = '/login.aspx';
      return;
    }
    return Promise.reject(error);
  }
);

const StudentVideoStats = () => {
  const [semesters, setSemesters] = useState([]);
  const [moduleSemesters, setModuleSemesters] = useState([]);
  const [educatorClasses, setEducatorClasses] = useState([]);
  const [msTopics, setMsTopics] = useState([]);
  const [selectedSemesterId, setSelectedSemesterId] = useState('');
  const [selectedModuleSemesterId, setSelectedModuleSemesterId] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [displayedTopicId, setDisplayedTopicId] = useState(null); // Track which topic is currently displayed
  const [videoStats, setVideoStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const token = getTokenForRequest();
      const response = await axios.get('/webservices/Report.svc/moduleSemesters', {
        params: {
          token: encodeToken(token)
        }
      });
      
      const { AllSemesters, ModuleSemesters } = response.data;
      setSemesters(AllSemesters);
      setModuleSemesters(ModuleSemesters);

      // Set initial semester
      const currentSemester = AllSemesters.find(s => s.Effective) || AllSemesters[0];
      if (currentSemester) {
        setSelectedSemesterId(currentSemester.ID.toString());
        const moduleSemester = ModuleSemesters.find(m => m.SemesterID === currentSemester.ID);
        if (moduleSemester) {
          setSelectedModuleSemesterId(moduleSemester.ID.toString());
          loadMsDataItems(moduleSemester.ID.toString());
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadMsDataItems = async (moduleSemesterId) => {
    try {
      const token = getTokenForRequest();
      const response = await axios.get('/webservices/Report.svc/msItemByID', {
        params: {
          token: encodeToken(token),
          moduleSemesterID: moduleSemesterId
        }
      });

      if (response.data) {
        const { Classes, Topics } = response.data;
        
        // Handle Classes dropdown
        if (Classes.length === 1) {
          // If only one class, use it directly
          setEducatorClasses(Classes);
          setSelectedClassId(Classes[0].ClassID.toString());
        } else {
          // If multiple classes, add "All" option
          setEducatorClasses([{ ClassID: -1, ClassName: 'All' }, ...Classes]);
          setSelectedClassId('-1');
        }

        // Handle Topics dropdown - show topics with videos and "All" option
        const topicsWithVideo = Topics.filter(t => t.HasVideo);
        if (topicsWithVideo.length > 0) {
          setMsTopics([{ TopicID: -1, TopicName: 'All' }, ...topicsWithVideo]);
          setSelectedTopic('-1'); // Default to "All"
        } else {
          setMsTopics([]);
          setSelectedTopic('');
        }
      }
    } catch (error) {
      console.error('Error loading MS data:', error);
    }
  };

  const fetchVideoStats = async (topicId) => {
    if (!selectedClassId || !selectedModuleSemesterId || !topicId) return;

    setLoading(true);
    try {
      const token = getTokenForRequest();
      
      const response = await axios.post('/webservices/Report.svc/LessonVideoStatistics', {
        token: encodeToken(token),
        classID: selectedClassId,
        msID: selectedModuleSemesterId,
        topicID: topicId
      });

      setVideoStats(response.data);
    } catch (error) {
      console.error('Error getting video stats:', error);
      alert('Failed to get report. Please try again!');
    } finally {
      setLoading(false);
    }
  };

  const getStudentVideoStatsReport = async () => {
    if (!selectedClassId || !selectedModuleSemesterId || !selectedTopic) return;

    // If "All" is selected, use the first topic's ID or the currently displayed topic
    let topicIDToSend = selectedTopic;
    if (selectedTopic === '-1' && msTopics.length > 1) {
      // If a topic is already displayed, use it; otherwise use the first topic
      if (displayedTopicId) {
        topicIDToSend = displayedTopicId.toString();
      } else {
        const firstTopic = msTopics.find(t => t.TopicID !== -1);
        if (firstTopic) {
          topicIDToSend = firstTopic.TopicID.toString();
          setDisplayedTopicId(firstTopic.TopicID);
        }
      }
    } else if (selectedTopic !== '-1') {
      // When a specific topic is selected from dropdown, reset displayedTopicId
      setDisplayedTopicId(null);
    }
    
    await fetchVideoStats(topicIDToSend);
  };

  const handleSemesterChange = (e) => {
    const semesterId = e.target.value;
    setSelectedSemesterId(semesterId);
    setDisplayedTopicId(null); // Reset displayed topic when semester changes
    
    const moduleSemester = moduleSemesters.find(m => m.SemesterID === parseInt(semesterId));
    if (moduleSemester) {
      setSelectedModuleSemesterId(moduleSemester.ID.toString());
      loadMsDataItems(moduleSemester.ID.toString());
    }
  };

  const handleModuleChange = (e) => {
    const moduleId = e.target.value;
    setSelectedModuleSemesterId(moduleId);
    setDisplayedTopicId(null); // Reset displayed topic when module changes
    loadMsDataItems(moduleId);
  };

  return (
    <div className="container-fluid p-4">
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label className="form-label fw-bold">Semester</label>
            <select 
              className="form-select" 
              value={selectedSemesterId}
              onChange={handleSemesterChange}
            >
              {semesters.map(semester => (
                <option key={semester.ID} value={semester.ID}>
                  {semester.Name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label className="form-label fw-bold">Module</label>
            <select 
              className="form-select" 
              value={selectedModuleSemesterId}
              onChange={handleModuleChange}
            >
              {moduleSemesters
                .filter(ms => ms.SemesterID === parseInt(selectedSemesterId))
                .map(ms => (
                  <option key={ms.ID} value={ms.ID}>
                    {ms.Module}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group mb-3">
            <label className="form-label fw-bold">Class</label>
            <select 
              className="form-select" 
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
            >
              {educatorClasses.map(eduClass => (
                <option key={eduClass.ClassID} value={eduClass.ClassID}>
                  {eduClass.ClassName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label className="form-label fw-bold">Topic</label>
            <select 
              className="form-select" 
              value={selectedTopic}
              onChange={(e) => {
                const newTopic = e.target.value;
                setSelectedTopic(newTopic);
                // Reset displayed topic when user selects a specific topic from dropdown
                if (newTopic !== '-1') {
                  setDisplayedTopicId(null);
                }
              }}
            >
              {msTopics.map(topic => (
                <option key={topic.TopicID} value={topic.TopicID}>
                  {topic.TopicName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <button 
            className="btn btn-primary" 
            disabled={!(selectedClassId && selectedModuleSemesterId && selectedTopic)}
            onClick={getStudentVideoStatsReport}
          >
            Get Report
          </button>
        </div>
      </div>

      {loading && (
        <div className="position-fixed top-50 start-50 translate-middle bg-white p-4 rounded shadow">
          <div className="d-flex flex-column align-items-center">
            <img className="mb-2" src="/img/loading_spinner.gif" alt="Loading" style={{ width: '50px', height: '50px' }} />
            <div className="fw-bold">Loading</div>
          </div>
        </div>
      )}

      {videoStats && (
        <div className="row">
          <div className="col-12">
            <h3 className="mb-4">Video Viewing Status for Deployed Topics</h3>
            
            {/* Display topic links when "All" is selected */}
            {selectedTopic === '-1' && msTopics.length > 1 && (
              <div className="mb-3" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {msTopics
                  .filter(topic => topic.TopicID !== -1)
                  .map(topic => {
                    const isSelected = displayedTopicId === topic.TopicID;
                    return (
                      <span
                        key={topic.TopicID}
                        onClick={() => {
                          // Keep dropdown on "All", but track which topic is displayed
                          setDisplayedTopicId(topic.TopicID);
                          fetchVideoStats(topic.TopicID.toString());
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: isSelected ? '#007bff' : '#f0f0f0',
                          color: isSelected ? '#fff' : '#000',
                          border: isSelected ? '1px solid #007bff' : '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'inline-block',
                          transition: 'all 0.2s',
                          fontWeight: isSelected ? 'bold' : 'normal'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = '#e0e0e0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = '#f0f0f0';
                          }
                        }}
                      >
                        {topic.TopicName}
                      </span>
                    );
                  })}
              </div>
            )}
            
            {!videoStats.VideoInfos || videoStats.VideoInfos.length === 0 ? (
              <h4 className="text-muted">No video is uploaded for this topic.</h4>
            ) : !videoStats.StudentViews || videoStats.StudentViews.length === 0 ? (
              <h4 className="text-muted">No student watched any video</h4>
            ) : (
              <div className="table-responsive" style={{ overflowX: 'auto', width: '100%' }}>
                <table className="table table-bordered table-hover" style={{ minWidth: 900 }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 200, position: 'sticky', left: 0, background: '#fff', zIndex: 2 }}>Video</th>
                      {videoStats.VideoInfos.map((video, index) => {
                        const screenshotUrl = video.Screenshot 
                          ? (video.Screenshot.startsWith('http') 
                              ? video.Screenshot 
                              : `https://d6adgef862n1y.cloudfront.net/${video.Screenshot}`)
                          : null;
                        const isVideoFile = screenshotUrl && /\.(mp4|webm|ogg|mov|avi)$/i.test(video.Screenshot);
                        const videoPath = video.Location || video.Filepath;
                        const videoUrl = videoPath 
                          ? (videoPath.startsWith('http') 
                              ? videoPath 
                              : `https://d6adgef862n1y.cloudfront.net/${videoPath}`)
                          : (isVideoFile ? screenshotUrl : null);
                        
                        return (
                          <th key={index} style={{ minWidth: 200 }}>
                            <div className="text-center">
                              {isVideoFile && videoUrl ? (
                                <video
                                  className="img-fluid"
                                  style={{ cursor: 'pointer', maxWidth: '180px', maxHeight: '120px', objectFit: 'cover', borderRadius: '4px' }}
                                  src={videoUrl}
                                  preload="metadata"
                                  muted
                                  onClick={() => setPreviewVideo(video)}
                                  onMouseEnter={(e) => {
                                    // Play on hover for preview
                                    if (e.target.paused) {
                                      e.target.play().catch(() => {});
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    // Pause and reset on mouse leave
                                    e.target.pause();
                                    e.target.currentTime = 0;
                                  }}
                                />
                              ) : screenshotUrl && !isVideoFile ? (
                                <img 
                                  className="img-fluid" 
                                  style={{ cursor: 'pointer', maxWidth: '180px' }}
                                  src={screenshotUrl}
                                  alt={stripHtml(video.Description)}
                                  title={stripHtml(video.Description)}
                                  onClick={() => setPreviewVideo(video)}
                                />
                              ) : (
                                <div
                                  style={{ 
                                    width: '180px', 
                                    height: '120px', 
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    cursor: 'pointer',
                                    margin: '0 auto',
                                    borderRadius: '4px',
                                    color: 'white'
                                  }}
                                  onClick={() => setPreviewVideo(video)}
                                  title={stripHtml(video.Description)}
                                >
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5v14l11-7z" fill="currentColor"/>
                                  </svg>
                                  <span style={{ marginTop: '8px', fontSize: '12px' }}>Video</span>
                                </div>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {videoStats.StudentViews?.map((student, index) => (
                      <tr key={index}>
                        <td style={{ minWidth: 200, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{student.DisplayName}</td>
                        {student.VideoStatistics?.map((stat, idx) => (
                          <td 
                            key={idx}
                            className="text-center"
                            style={{ 
                              cursor: 'pointer',
                              backgroundColor: stat.Percentage === 0 ? '#ff3333' : undefined,
                              minWidth: 200
                            }}
                            onClick={() => setPreviewVideo(videoStats.VideoInfos[idx])}
                          >
                            {stat.Percentage}%
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal for scaffold preview */}
      {previewVideo && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ maxWidth: 820, marginTop: 80 }} role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{stripHtml(previewVideo.Description)}</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewVideo(null)}></button>
              </div>
              <div className="modal-body d-flex justify-content-center align-items-center" style={{ minHeight: 470 }}>
                <div style={{ width: 800, height: 450, background: '#000', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(previewVideo.Location || previewVideo.Filepath) ? (
                    <video
                      controls
                      style={{ width: 800, height: 450, background: '#000', borderRadius: 8 }}
                    >
                      <source
                        src={
                          (previewVideo.Location || previewVideo.Filepath)
                            ? (previewVideo.Location || previewVideo.Filepath).startsWith('http')
                              ? (previewVideo.Location || previewVideo.Filepath)
                              : `https://d6adgef862n1y.cloudfront.net/${(previewVideo.Location || previewVideo.Filepath)}`
                            : ''
                        }
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : previewVideo.Screenshot ? (
                    <img
                      src={
                        previewVideo.Screenshot.startsWith('http')
                          ? previewVideo.Screenshot
                          : `https://d6adgef862n1y.cloudfront.net/${previewVideo.Screenshot}`
                      }
                      alt={stripHtml(previewVideo.Description)}
                      style={{ width: 800, height: 450, objectFit: 'contain', background: '#000', borderRadius: 8 }}
                    />
                  ) : (
                    <div style={{ color: 'white' }}>No video available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentVideoStats; 